// The overview header's readiness summary. Green when the PR is approved,
// checks pass, GitHub would allow the merge and no unresolved threads remain.
import type { PullRequestData } from '../api/queries';

export type Tone = 'good' | 'bad' | 'pending' | 'neutral';

/** What a readiness item reports on (and where the header sends you for it). */
export type ReadinessKind = 'decision' | 'approvals' | 'checks' | 'threads' | 'merge';

export interface ReadinessItem {
  kind: ReadinessKind;
  label: string;
  tone: Tone;
}

/** Where a pull request stands, most decisive first (see `readiness`). */
export type ReadinessStatus = 'merged' | 'closed' | 'draft' | 'ready' | 'unresolved' | 'changesRequested' | 'inReview';

/** Each status's headline: the only place its wording lives. */
export const HEADLINE: Record<ReadinessStatus, string> = {
  merged: 'Merged',
  closed: 'Closed',
  draft: 'Draft',
  ready: 'Ready to merge',
  unresolved: 'Waiting on unresolved threads',
  changesRequested: 'Changes requested',
  inReview: 'In review',
};

export interface Readiness {
  /** Open, approved, checks passing, mergeable and nothing unresolved (status 'ready'). */
  ready: boolean;
  status: ReadinessStatus;
  /** HEADLINE[status]. */
  headline: string;
  items: ReadinessItem[];
  approvals: number;
  required: number | null;
}

const MERGE_ALLOWED = new Set(['CLEAN', 'HAS_HOOKS', 'UNSTABLE']);

export function checksTone(state: string | null | undefined): Tone {
  switch (state) {
    case 'SUCCESS':
      return 'good';
    case 'FAILURE':
    case 'ERROR':
      return 'bad';
    case 'PENDING':
    case 'EXPECTED':
      return 'pending';
    default:
      return 'neutral';
  }
}

/**
 * The review verdict. GitHub's `reviewDecision` when it gives one; it gives
 * none when branch protection does not require reviews, and then the PR is
 * approved when someone's latest review approves and nobody's latest review
 * requests changes.
 */
export function reviewVerdict(pr: PullRequestData): { approvals: number; approved: boolean; changesRequested: boolean } {
  const latest = pr.latestReviews.nodes;
  const approvals = latest.filter((r) => r.state === 'APPROVED').length;
  if (pr.reviewDecision !== null)
    return {
      approvals,
      approved: pr.reviewDecision === 'APPROVED',
      changesRequested: pr.reviewDecision === 'CHANGES_REQUESTED',
    };
  const changesRequested = latest.some((r) => r.state === 'CHANGES_REQUESTED');
  return { approvals, approved: approvals > 0 && !changesRequested, changesRequested };
}

export function readiness(pr: PullRequestData, unresolved: number): Readiness {
  const { approvals, approved, changesRequested } = reviewVerdict(pr);
  const required = pr.baseRef?.branchProtectionRule?.requiredApprovingReviewCount ?? null;
  const rollup = pr.commits.nodes[0]?.commit.statusCheckRollup?.state ?? null;
  const items: ReadinessItem[] = [];

  const decision =
    pr.reviewDecision === 'APPROVED'
      ? { label: 'Approved', tone: 'good' as Tone }
      : pr.reviewDecision === 'CHANGES_REQUESTED'
        ? { label: 'Changes requested', tone: 'bad' as Tone }
        : pr.reviewDecision === 'REVIEW_REQUIRED'
          ? { label: 'Review required', tone: 'pending' as Tone }
          : changesRequested
            ? { label: 'Changes requested', tone: 'bad' as Tone }
            : { label: approvals ? 'Approved' : 'No reviews yet', tone: (approvals ? 'good' : 'neutral') as Tone };
  items.push({ kind: 'decision', ...decision });
  items.push({
    kind: 'approvals',
    label: required !== null ? `${approvals} of ${required} approvals` : `${approvals} approval${approvals === 1 ? '' : 's'}`,
    tone: required !== null ? (approvals >= required ? 'good' : 'pending') : approvals ? 'good' : 'neutral',
  });
  if (rollup)
    items.push({
      kind: 'checks',
      label: rollup === 'SUCCESS' ? 'Checks passed' : rollup === 'PENDING' || rollup === 'EXPECTED' ? 'Checks running' : 'Checks failing',
      tone: checksTone(rollup),
    });
  items.push({
    kind: 'threads',
    label: unresolved ? `${unresolved} unresolved thread${unresolved === 1 ? '' : 's'}` : 'No unresolved threads',
    tone: unresolved ? 'bad' : 'good',
  });
  const mergeLabel: Record<string, [string, Tone]> = {
    CLEAN: ['Mergeable', 'good'],
    HAS_HOOKS: ['Mergeable', 'good'],
    UNSTABLE: ['Mergeable (non-required checks failing)', 'pending'],
    BEHIND: ['Branch is behind base', 'pending'],
    BLOCKED: ['Merge blocked', 'bad'],
    DIRTY: ['Merge conflicts', 'bad'],
    DRAFT: ['Draft', 'neutral'],
    UNKNOWN: ['Checking mergeability…', 'neutral'],
  };
  if (pr.state === 'OPEN') {
    const [label, tone] = mergeLabel[pr.mergeStateStatus] ?? ['Unknown', 'neutral'];
    items.push({ kind: 'merge', label, tone });
  }

  // Green only when approved, checks pass (or there are none), nothing is
  // unresolved and GitHub reports the PR mergeable. Without branch protection
  // GitHub reports CLEAN regardless of reviews and checks, so both are checked here.
  const checksPass = rollup === null || rollup === 'SUCCESS';
  const ready =
    pr.state === 'OPEN' &&
    !pr.isDraft &&
    approved &&
    checksPass &&
    pr.mergeable === 'MERGEABLE' &&
    MERGE_ALLOWED.has(pr.mergeStateStatus) &&
    unresolved === 0;
  const status: ReadinessStatus =
    pr.state === 'MERGED'
      ? 'merged'
      : pr.state === 'CLOSED'
        ? 'closed'
        : pr.isDraft
          ? 'draft'
          : ready
            ? 'ready'
            : unresolved
              ? 'unresolved'
              : changesRequested
                ? 'changesRequested'
                : 'inReview';
  return { ready, status, headline: HEADLINE[status], items, approvals, required };
}

export function canMerge(pr: PullRequestData, allowMerge: boolean): boolean {
  return allowMerge && pr.state === 'OPEN' && !pr.isDraft && pr.mergeable === 'MERGEABLE' && MERGE_ALLOWED.has(pr.mergeStateStatus);
}

/**
 * Why the pull request cannot be merged from changelist right now, as
 * sentences for the merge card: GitHub's merge state (conflicts, branch
 * behind, branch protection, still computing), what readiness lacks
 * (`mergeBlockers`), and an organization that turned merging off. Empty
 * when nothing stands in the way. Closed, merged and draft pull requests
 * say so instead.
 */
export function mergeExplanation(pr: PullRequestData, unresolved: number, allowMerge: boolean): string[] {
  if (pr.state === 'MERGED') return ['It is already merged.'];
  if (pr.state === 'CLOSED') return ['It is closed.'];
  if (pr.isDraft) return ['It is a draft: mark it ready for review first.'];
  const out: string[] = [];
  const github: Record<string, string> = {
    BLOCKED: 'GitHub blocks the merge: branch protection rules are not met (required reviews or required checks).',
    DIRTY: 'The branch has merge conflicts with the base branch; resolve them first.',
    BEHIND: 'The branch is behind the base branch; update it first.',
    UNKNOWN: 'GitHub is still working out whether it can be merged.',
  };
  if (pr.mergeable === 'CONFLICTING' && pr.mergeStateStatus !== 'DIRTY') out.push(github.DIRTY);
  if (github[pr.mergeStateStatus]) out.push(github[pr.mergeStateStatus]);
  for (const b of mergeBlockers(pr, unresolved)) out.push(b.charAt(0).toUpperCase() + b.slice(1) + '.');
  if (!allowMerge) out.push('This organization turned off merging from Changeling; merge on GitHub.');
  return out;
}

/**
 * What keeps an open pull request from being ready although GitHub may
 * allow the merge (see `readiness`): missing approval, checks that have not
 * passed, unresolved threads. Empty when none of these applies.
 */
export function mergeBlockers(pr: PullRequestData, unresolved: number): string[] {
  const out: string[] = [];
  const { approved, changesRequested } = reviewVerdict(pr);
  if (!approved) out.push(changesRequested ? 'changes were requested' : 'it is not approved');
  const rollup = pr.commits.nodes[0]?.commit.statusCheckRollup?.state ?? null;
  if (rollup !== null && rollup !== 'SUCCESS')
    out.push(rollup === 'PENDING' || rollup === 'EXPECTED' ? 'checks are still running' : 'checks are failing');
  if (unresolved) out.push(`${unresolved} unresolved thread${unresolved === 1 ? '' : 's'}`);
  return out;
}
